import api from './client';
import { API_ENDPOINTS } from './endpoints';

export interface TicketApiItem {
  _id: string;
  patient_account_number?: string;
  patient_name?: string;
  payer_name?: string;
  payer_type?: string;
  dos_from?: string;
  submission_date?: string;
  claim_received_date?: string;
  assigned_user?: string;
  assigned_date?: string;
  rejection_flag?: boolean;
  rejection_reason?: string;
  // Set by QC/Lead/Manager when they reject a closed ticket during the
  // approval workflow (approvals.controller.js) — the actual "why was this
  // rejected" comment. Distinct from rejection_reason above, which is a
  // medical-billing claim-submission field, not an approval one.
  approval_remarks?: string;
  resubmission_date?: string;
  tat_days?: number;
  quality_score?: number;
  error_flag?: boolean;
  error_reason?: string;
  remarks?: string;
  charge_amount?: number;
  cpt_codes?: string[];
  icd_codes?: string[];
  claim_number?: string;
  claim_id?: string;
  policy_number?: string;
  claimant_name?: string;
  ticket_status?: string;
  status?: string;
  submission_status?: string;
  resubmitted_count?: number;
  priority?: string;
  claim_amount?: number;
  dob?: string;
  dos?: string;
  dos_to?: string;
  provider_name?: string;
  practice_name?: string;
  member_id?: string;
  due_date?: string;
  custom_ticket_id?: string;
  ticket_number?: string;
  department_name?: string;
  assigned_to_name?: string;
  assignee_name?: string;
  assigned_user_email?: string;
  assigned_to_email?: string;
  assignee_email?: string;
  insurance_party?: string;
  insurance_party_name?: string;
  client_name?: string;
  current_assignee_id?: string;
  current_assignee_name?: string;
  // Server-computed convenience fields (see ticket.controller.js getAllTickets) —
  // not stored on the ticket itself, just resolved fresh on every fetch.
  current_assignee_reporting_lead_name?: string;
  resolved_by_id?: string;
  resolved_by_name?: string;
  resolved_at?: string;
  follow_up_date?: string;
  location?: string;
  account_no?: string;
  cpt?: string;
  rendering_provider?: string;
  current_resp?: string;
  age?: string;
  age_group?: string;
  ar_type?: string;
  user_comments?: string;
  category?: string;
  sub_category?: string;
  action_taken?: string;
  exception_non_exception?: string;
  worked_date?: string;
  worked_mode?: string;
  emp_id?: string;
  employee_name?: string;
  reporting_manager?: string;
  process_hod?: string;
  audit_queue?: string;
  auditor_emp_id?: string;
  auditor_name?: string;
  audit_status?: string;
  error_value?: string;
  error_classification?: string;
  error_type?: string;
  error_description?: string;
  auditor_comment?: string;
  auditor_date?: string;
  auditor_error?: boolean | string;
  rebuttal_comment?: string;
  qa_response?: string;
  qced?: boolean | string;
  week_no?: string;
  follow_up?: {
    basic?: {
      follow_up_date?: string | null;
      next_follow_up_date?: string | null;
      next_follow_up_days?: number | null;
      resolution_date?: string | null;
      assigned_to_name?: string;
      policy_member_id?: string;
      provider_practice_name?: string;
      description?: string;
      key_points?: string;
      task_bucket?: string;
      payment_status?: string;
      // Independent of the ticket's own ticket_status — see TicketBucketPage's
      // currentStatusOptions/workStatusOptions.
      work_status?: string;
    };
    financial?: {
      billed_amount?: number;
      allowed_amount?: number;
      insurance_paid_amount?: number;
      patient_responsibility?: number;
      copay_amount?: number;
      deductible_amount?: number;
      coinsurance_amount?: number;
      adjustment_amount?: number;
      write_off_amount?: number;
      outstanding_balance?: number;
      expected_reimbursement?: number;
      received_amount?: number;
      denied_amount?: number;
      pending_amount?: number;
    };
    call_log?: {
      call_date?: string | null;
      call_start_time?: string;
      call_end_time?: string;
      call_duration?: string;
      call_reference_number?: string;
      call_type?: string;
    };
    accounts_receivable?: {
      location?: string;
      account_no?: string;
      cpt?: string;
      rendering_provider?: string;
      current_resp?: string;
      age?: string;
      age_group?: string;
      ar_type?: string;
      user_comments?: string;
      category?: string;
      sub_category?: string;
      action_taken?: string;
      exception_non_exception?: string;
      worked_date?: string | null;
      worked_mode?: string;
      emp_id?: string;
      employee_name?: string;
      reporting_manager?: string;
      team_lead?: string;
      process_hod?: string;
      audit_queue?: string;
      auditor_emp_id?: string;
      auditor_name?: string;
      audit_status?: string;
      error_value?: string;
      error_classification?: string;
      error_type?: string;
      error_description?: string;
      auditor_comment?: string;
      auditor_date?: string | null;
      auditor_error?: boolean | string;
      rebuttal_comment?: string;
      qa_response?: string;
      qced?: boolean | string;
      week_no?: string;
    };
  };
  title?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  comments?: TicketCommentApiItem[];
  meta?: {
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface TicketCommentApiItem {
  comment: string;
  commented_by_id?: string;
  commented_by_name: string;
  createdAt?: string;
}

export interface TicketActivityApiItem {
  _id: string;
  custom_activity_id: string;
  action: string;
  actor_user_name?: string;
  remarks?: string;
  action_at?: string;
}

export interface GetTicketActivityResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    activity?: TicketActivityApiItem[];
  };
}

export const getTicketActivity = async (ticketId: string): Promise<GetTicketActivityResponse> => {
  return api.get<GetTicketActivityResponse>(`${API_ENDPOINTS.tickets.list}/${ticketId}/activity`);
};

export interface TicketsListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    tickets?: TicketApiItem[];
    count?: number;
  };
}

export interface UploadClaimsFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadMedicalBillingFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadMedicalCodingFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadPriorAuthorizationFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadCredentialingFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadEligibilityBenefitsFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadAccountsReceivableFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadDenialManagementFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadPatientBillingFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface MedicalBillingDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface MedicalCodingDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface PriorAuthorizationDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface CredentialingDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface EligibilityBenefitsDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface AccountsReceivableDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface DenialManagementDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface PatientBillingDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface UploadClientAssistanceFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface ClientAssistanceDepartmentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface AssignTicketPayload {
  assigned_to_id: string;
  assignment_type: 'manual' | 'auto';
  notes?: string;
}

export interface AssignTicketResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface TicketStatusUpdateResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export type TaskBucket =
  | ''
  | 'Billing Tasks'
  | 'Credentialing Tasks'
  | 'Payment Posting Tasks'
  | 'Patient Calling Tasks'
  | 'Coding Tasks'
  | 'Client Assistance Tasks'
  | 'Calling Assistance Tasks'
  | 'Aging Team Tasks';

export type PaymentStatus = '' | 'paid' | 'posted' | 'adjusted' | 'denied';

export interface SaveTicketFollowUpPayload {
  ticket_status: 'open' | 'assigned' | 'in_progress' | 'completed' | 'paid' | 'on_hold' | 'follow_up' | 'closed' | 'rejected';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  department_name?: string;
  current_assignee_id: string | null;
  current_assignee_name: string;
  assigned_to_email: string;
  provider_name: string;
  practice_name: string;
  dos: string | null;
  // Appended to the ticket's comment history rather than overwriting it — see
  // Ticket.comments on the backend. Omit/leave blank to save follow-up details
  // without adding a new comment.
  new_comment?: string;
  follow_up: {
    basic: {
      follow_up_date: string | null;
      next_follow_up_date: string | null;
      next_follow_up_days?: number | null;
      resolution_date: string | null;
      assigned_to_name: string;
      policy_member_id: string;
      provider_practice_name: string;
      description: string;
      key_points: string;
      task_bucket: TaskBucket;
      payment_status: PaymentStatus;
      work_status?: 'assigned' | 'in_progress' | 'completed' | 'paid' | '';
    };
    financial: {
      billed_amount: number;
      allowed_amount: number;
      insurance_paid_amount: number;
      patient_responsibility: number;
      copay_amount: number;
      deductible_amount: number;
      coinsurance_amount: number;
      adjustment_amount: number;
      write_off_amount: number;
      outstanding_balance: number;
      expected_reimbursement: number;
      received_amount: number;
      denied_amount: number;
      pending_amount: number;
    };
    call_log: {
      call_date: string | null;
      call_start_time: string;
      call_end_time: string;
      call_duration: string;
      call_reference_number: string;
      call_type: string;
    };
    accounts_receivable?: {
      location: string;
      account_no: string;
      cpt: string;
      rendering_provider: string;
      current_resp: string;
      age: string;
      age_group: string;
      ar_type: string;
      user_comments: string;
      category: string;
      sub_category: string;
      action_taken: string;
      exception_non_exception: string;
      worked_date: string | null;
      worked_mode: string;
      emp_id: string;
      employee_name: string;
      reporting_manager: string;
      team_lead?: string;
      process_hod: string;
      audit_queue: string;
      auditor_emp_id: string;
      auditor_name: string;
      audit_status: string;
      error_value: string;
      error_classification: string;
      error_type: string;
      error_description: string;
      auditor_comment: string;
      auditor_date: string | null;
      auditor_error: boolean;
      rebuttal_comment: string;
      qa_response: string;
      qced: boolean;
      week_no: string;
    };
  };
}

export interface SaveTicketFollowUpResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface SaveMedicalBillingFollowUpPayload {
  claim_id: string;
  patient_account_number: string;
  patient_name: string;
  provider_name: string;
  practice_name: string;
  payer_name: string;
  payer_type: string;
  dos_from: string | null;
  dos_to: string | null;
  charge_amount: number;
  claim_amount: number;
  cpt_codes: string[];
  icd_codes: string[];
  submission_date: string | null;
  claim_received_date: string | null;
  assigned_user: string;
  assigned_date: string | null;
  status: string;
  submission_status: string;
  rejection_flag: boolean;
  rejection_reason: string;
  resubmission_date: string | null;
  tat_days: number;
  quality_score: number;
  error_flag: boolean;
  error_reason: string;
  remarks: string;
}

export interface SaveMedicalBillingFollowUpResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export const getTickets = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  ticket_status?: string;
  department_name?: string;
  assignee_id?: string;
  'meta.status'?: 'active' | 'inactive';
  date_from?: string;
  date_to?: string;
}): Promise<TicketsListResponse> => {
  return api.get<TicketsListResponse>(API_ENDPOINTS.tickets.list, { params });
};

export interface TicketStatusCountsResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    counts?: {
      open?: number;
      assigned?: number;
      in_progress?: number;
      completed?: number;
      paid?: number;
      on_hold?: number;
      follow_up?: number;
      closed?: number;
      rejected?: number;
    };
  };
}

export const getTicketStatusCounts = async (params?: {
  search?: string;
  department_name?: string;
  assignee_id?: string;
}): Promise<TicketStatusCountsResponse> => {
  return api.get<TicketStatusCountsResponse>(API_ENDPOINTS.tickets.statusCounts, { params });
};

export const uploadClaimsFile = async (payload: {
  file: File;
  department_name: string;
  auto_assign: boolean;
}): Promise<UploadClaimsFileResponse> => {
  const formData = new FormData();
  formData.append('department_name', payload.department_name);
  formData.append('auto_assign', String(payload.auto_assign));
  formData.append('file', payload.file);

  return api.post<UploadClaimsFileResponse, FormData>(API_ENDPOINTS.tickets.uploadClaimsFile, formData);
};

export const uploadMedicalBillingFile = async (payload: {
  file: File;
  auto_assign: boolean;
  department_name?: string;
}): Promise<UploadMedicalBillingFileResponse> => {
  const formData = new FormData();
  formData.append('auto_assign', String(payload.auto_assign));
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadMedicalBillingFileResponse, FormData>(API_ENDPOINTS.departments.uploadMedicalBillingFile, formData);
};

export const uploadMedicalCodingFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadMedicalCodingFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadMedicalCodingFileResponse, FormData>(API_ENDPOINTS.departments.uploadMedicalCodingFile, formData);
};

export const uploadPriorAuthorizationFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadPriorAuthorizationFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadPriorAuthorizationFileResponse, FormData>(API_ENDPOINTS.departments.uploadPriorAuthorizationFile, formData);
};

export const uploadCredentialingFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadCredentialingFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadCredentialingFileResponse, FormData>(API_ENDPOINTS.departments.uploadCredentialingFile, formData);
};

export const uploadEligibilityBenefitsFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadEligibilityBenefitsFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadEligibilityBenefitsFileResponse, FormData>(API_ENDPOINTS.departments.uploadEligibilityBenefitsFile, formData);
};

export const uploadAccountsReceivableFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
  client_id?: string;
  mandatory_fields?: string[];
}): Promise<UploadAccountsReceivableFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  if (payload.client_id) {
    formData.append('client_id', payload.client_id);
  }
  if (payload.mandatory_fields?.length) {
    formData.append('mandatory_fields', JSON.stringify(payload.mandatory_fields));
  }
  formData.append('file', payload.file);

  return api.post<UploadAccountsReceivableFileResponse, FormData>(API_ENDPOINTS.departments.uploadAccountsReceivableFile, formData);
};

export const uploadDenialManagementFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadDenialManagementFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadDenialManagementFileResponse, FormData>(API_ENDPOINTS.departments.uploadDenialManagementFile, formData);
};

export const uploadPatientBillingFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadPatientBillingFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadPatientBillingFileResponse, FormData>(API_ENDPOINTS.departments.uploadPatientBillingFile, formData);
};

export const uploadClientAssistanceFile = async (payload: {
  file: File;
  auto_assign?: boolean;
  department_name?: string;
}): Promise<UploadClientAssistanceFileResponse> => {
  const formData = new FormData();
  if (typeof payload.auto_assign === 'boolean') {
    formData.append('auto_assign', String(payload.auto_assign));
  }
  if (payload.department_name) {
    formData.append('department_name', payload.department_name);
  }
  formData.append('file', payload.file);

  return api.post<UploadClientAssistanceFileResponse, FormData>(API_ENDPOINTS.departments.uploadClientAssistanceFile, formData);
};

export const getMedicalBillingDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<MedicalBillingDepartmentResponse> => {
  return api.get<MedicalBillingDepartmentResponse>(API_ENDPOINTS.departments.medicalBillingData, { params });
};

export const getMedicalCodingDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<MedicalCodingDepartmentResponse> => {
  return api.get<MedicalCodingDepartmentResponse>(API_ENDPOINTS.departments.medicalCodingData, { params });
};

export const getPriorAuthorizationDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<PriorAuthorizationDepartmentResponse> => {
  return api.get<PriorAuthorizationDepartmentResponse>(API_ENDPOINTS.departments.priorAuthorizationData, { params });
};

export const getCredentialingDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<CredentialingDepartmentResponse> => {
  return api.get<CredentialingDepartmentResponse>(API_ENDPOINTS.departments.credentialingData, { params });
};

export const getEligibilityBenefitsDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<EligibilityBenefitsDepartmentResponse> => {
  return api.get<EligibilityBenefitsDepartmentResponse>(API_ENDPOINTS.departments.eligibilityBenefitsData, { params });
};

export const getAccountsReceivableDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<AccountsReceivableDepartmentResponse> => {
  return api.get<AccountsReceivableDepartmentResponse>(API_ENDPOINTS.departments.accountsReceivableData, { params });
};

export interface UploadBatchApiItem {
  _id: string;
  custom_batch_id: string;
  source_file_name?: string;
  uploaded_by_name?: string;
  total_records?: number;
  success_records?: number;
  failed_records?: number;
  createdAt?: string;
}

export interface AccountsReceivableUploadBatchesResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    latest_batch?: UploadBatchApiItem | null;
  };
}

export const getAccountsReceivableUploadBatches = async (params: {
  client_id: string;
}): Promise<AccountsReceivableUploadBatchesResponse> => {
  return api.get<AccountsReceivableUploadBatchesResponse>(API_ENDPOINTS.departments.accountsReceivableUploadBatches, {
    params,
  });
};

export interface DeleteUploadBatchResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    records_deleted?: number;
    tickets_deactivated?: number;
  };
}

export const deleteAccountsReceivableUploadBatch = async (batchId: string): Promise<DeleteUploadBatchResponse> => {
  return api.delete<DeleteUploadBatchResponse>(`${API_ENDPOINTS.departments.accountsReceivableUploadBatch}/${batchId}`);
};

export interface UnassignTicketResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    ticket?: TicketApiItem;
  };
}

export const unassignTicket = async (ticketId: string): Promise<UnassignTicketResponse> => {
  return api.post<UnassignTicketResponse, undefined>(`${API_ENDPOINTS.tickets.list}/${ticketId}/unassign`, undefined);
};

export const getDenialManagementDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<DenialManagementDepartmentResponse> => {
  return api.get<DenialManagementDepartmentResponse>(API_ENDPOINTS.departments.denialManagementData, { params });
};

export const getPatientBillingDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<PatientBillingDepartmentResponse> => {
  return api.get<PatientBillingDepartmentResponse>(API_ENDPOINTS.departments.patientBillingData, { params });
};

export const getClientAssistanceDepartmentData = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}): Promise<ClientAssistanceDepartmentResponse> => {
  return api.get<ClientAssistanceDepartmentResponse>(API_ENDPOINTS.departments.clientAssistanceData, { params });
};

export const assignTicket = async (
  ticketId: string,
  payload: AssignTicketPayload,
): Promise<AssignTicketResponse> => {
  return api.post<AssignTicketResponse, AssignTicketPayload>(`${API_ENDPOINTS.tickets.list}/${ticketId}/assign`, payload);
};

export interface AssignTicketsBulkPayload {
  ticket_ids: string[];
  assigned_to_id: string;
  assignment_type?: 'manual' | 'auto' | 'reassignment' | 'bulk';
  notes?: string;
}

export interface AssignTicketsBulkResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    assigned_count: number;
    failed: { id: string; reason: string }[];
  };
}

// Assigns several specific tickets to ONE user in a single call — used by the
// Bulk Assign Tasks dialog. The backend sends at most one email for the whole
// batch (a single tabular summary when more than one ticket is assigned), instead
// of one "ticket assigned" email per ticket.
export const assignTicketsBulk = async (
  payload: AssignTicketsBulkPayload,
): Promise<AssignTicketsBulkResponse> => {
  return api.post<AssignTicketsBulkResponse, AssignTicketsBulkPayload>(
    API_ENDPOINTS.tickets.bulkAssignSelected,
    payload,
  );
};

export interface TicketAssignmentRecord {
  _id: string;
  custom_assignment_id?: string;
  ticket_id: string;
  claim_number?: string;
  assigned_to_id: string;
  assigned_to_name?: string;
  department_name?: string;
  assignment_type?: 'auto' | 'manual' | 'reassignment' | 'bulk';
  assignment_status: 'active' | 'completed' | 'released';
  assigned_at?: string;
  notes?: string;
}

export interface TicketAssignmentsListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    assignments: TicketAssignmentRecord[];
    count: number;
  };
}

export const getTicketAssignments = async (params?: {
  ticket_id?: string;
  assigned_to_id?: string;
  assignment_status?: 'active' | 'completed' | 'released';
  page?: number;
  limit?: number;
}): Promise<TicketAssignmentsListResponse> => {
  return api.get<TicketAssignmentsListResponse>(API_ENDPOINTS.tickets.assignments, { params });
};

export interface UserTicketTrackerEntry {
  _id: string;
  assigned_to_name?: string;
  department_name?: string;
  total_assigned: number;
  in_progress: number;
  completed: number;
  tracked_seconds?: number;
}

export interface UserTicketTrackerResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    tracker: UserTicketTrackerEntry[];
  };
}

export const getUserTicketTracker = async (
  params?: { department_name?: string },
): Promise<UserTicketTrackerResponse> => {
  return api.get<UserTicketTrackerResponse>(API_ENDPOINTS.tickets.trackerUsers, { params });
};

export const startTicketWork = async (
  ticketId: string,
  payload: { remarks?: string } = {},
): Promise<TicketStatusUpdateResponse> => {
  return api.post<TicketStatusUpdateResponse, { remarks?: string }>(`${API_ENDPOINTS.tickets.list}/${ticketId}/start`, payload);
};

export const closeTicketWork = async (
  ticketId: string,
  payload: { remarks?: string } = {},
): Promise<TicketStatusUpdateResponse> => {
  return api.post<TicketStatusUpdateResponse, { remarks?: string }>(`${API_ENDPOINTS.tickets.list}/${ticketId}/close`, payload);
};

export const saveTicketFollowUp = async (
  ticketId: string,
  payload: SaveTicketFollowUpPayload,
): Promise<SaveTicketFollowUpResponse> => {
  return api.post<SaveTicketFollowUpResponse, SaveTicketFollowUpPayload>(`${API_ENDPOINTS.tickets.list}/${ticketId}/follow-up`, payload);
};

export const saveMedicalBillingFollowUp = async (
  medicalBillingId: string,
  payload: SaveMedicalBillingFollowUpPayload,
): Promise<SaveMedicalBillingFollowUpResponse> => {
  return api.post<SaveMedicalBillingFollowUpResponse, SaveMedicalBillingFollowUpPayload>(`${API_ENDPOINTS.departments.medicalBillingFollowUp}/${medicalBillingId}/follow-up`, payload);
};
